<script lang="ts" setup>
import Anonymus from '~/assets/images/token-gated-chats-and-streams/anonymus.svg'

import MoreEarnings from '~/assets/images/token-gated-chats-and-streams/more-earnings.svg'
import EarnFromReferrals from '~/assets/images/token-gated-chats-and-streams/earn-from-referrals.svg'
import ExpiringMemberships from '~/assets/images/token-gated-chats-and-streams/expiring-memberships.svg'
import TotalControl from '~/assets/images/token-gated-chats-and-streams/total-control.svg'
import CryptoModern from '~/assets/images/token-gated-chats-and-streams/crypto-modern.svg'
import ReceiveYour from '~/assets/images/token-gated-chats-and-streams/receive-your.svg'

const { t } = useI18n()

definePageMeta({
  layout: 'base',
})

const {
  public: { siteUrl },
} = useRuntimeConfig()

useSeoMeta({
  title: t('competitor-of-patreon-and-onlyfans'),
  description: t('the-future-of-content-monetization-is-here'),
  ogTitle: t('competitor-of-patreon-and-onlyfans'),
  ogDescription: t('the-future-of-content-monetization-is-here'),
  ogImage: `${siteUrl}/images/token-gated-chats-and-streams/preview.webp`,
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogType: 'website',
  twitterTitle: t('competitor-of-patreon-and-onlyfans'),
  twitterDescription: t('the-future-of-content-monetization-is-here'),
  twitterImage: `${siteUrl}/images/token-gated-chats-and-streams/preview.webp`,
  twitterCard: 'summary_large_image',
})

const chooseItems = [
  {
    id: '0',
    image: Anonymus,
    head: t('stay-anonymous-with-crypto'),
    description: t(
      'your-fans-value-privacy-with-crypto-transactions-their-identity-remains-concealed-ensuring-a-secure-and-private-experience'
    ),
  },
  {
    id: '1',
    image: MoreEarnings,
    head: t('more-earnings-minimal-fees'),
    description: t(
      'at-belong-we-take-only-3-of-your-earnings-unlike-platforms-like-patreon-you-keep-a-significant-share-of-your-hard-earned-money'
    ),
  },
  {
    id: '2',
    image: EarnFromReferrals,
    head: t('earn-from-referrals'),
    description: t(
      'refer-creators-to-belong-and-earn-a-3-commission-receive-it-instantly-with-every-creator-membership-pass-sale'
    ),
  },
  {
    id: '3',
    image: ExpiringMemberships,
    head: t('expiring-memberships'),
    description: t(
      'offer-limited-time-content-access-with-memberships-that-expire-ensuring-exclusivity-and-recurrent-engagement'
    ),
  },
  {
    id: '4',
    image: TotalControl,
    head: t('total-control-and-ownership'),
    description: t(
      'dictate-your-content-terms-set-your-prices-and-directly-engage-with-your-community'
    ),
  },
  {
    id: '5',
    image: CryptoModern,
    head: t('crypto-and-modern-payment-convenience'),
    description: t(
      'not-only-do-we-handle-crypto-transactions-but-we-also-accept-credit-cards-apple-pay-and-google-pay-for-instant-purchases-belong-makes-it-easy-for-every-user'
    ),
  },
  {
    id: '6',
    image: ReceiveYour,
    head: t('receive-your-funds-instantly'),
    description: t(
      'no-more-waiting-get-your-funds-immediately-in-your-crypto-wallet-bypassing-traditional-banking-systems-and-ensuring-privacy'
    ),
  },
]

const scrollY = ref(0)

const handleScroll = () => {
  scrollY.value = window.scrollY
}

const elementStyle = (index: number) => {
  const translateY = scrollY.value * (index * 0.5)
  return `transform: translateY(${translateY}px)`
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <section>
    <div
      class="relative rounded-5 overflow-hidden mx-5 max-w-309 h-144.5 xl:mx-auto"
    >
      <NuxtImg
        loading="lazy"
        v-motion
        :initial="{
          x: -200,
        }"
        :enter="{
          x: 0,
          transition: {
            duration: 5000,
          },
        }"
        alt="t('the-future-of-content-monetization-is-here-0')"
        class="min-w-360 h-144.5 object-cover"
        src="/images/token-gated-chats-and-streams/the-future.webp"
      />

      <img
        v-motion="{
          initial: {
            opacity: 0,
          },
          enter: {
            opacity: 1,
          },
        }"
        alt="t('the-future-of-content-monetization-is-here-1')"
        class="absolute top-0 max-w-92 max-h-92 object-cover top-0 bottom-0 m-auto w-[calc(100%_-_1.25rem)] left-2.5 right-2.5 lg:max-w-243 lg:max-h-243 lg:left-0 lg:right-0"
        src="~/assets/images/token-gated-chats-and-streams/the-future-circle.svg"
      />

      <div
        class="absolute left-5 bottom-8 max-w-74 lg:max-w-full lg:left-17.5 lg:bottom-18.5"
      >
        <span
          class="text-[#B696FF] text-[1.375rem] tracking-wide leading-normal lg:text-[2rem]"
        >
          Belong:</span
        >
        <div
          class="animation-block-text text-4xl leading-[2.75rem] font-300 !lg:text-[4.25rem] lg:leading-[4.625rem] lg:tracking-wide"
        >
          <div
            v-motion="{
              initial: {
                opacity: 0,
              },
              enter: {
                opacity: 1,
                transition: {
                  delay: 500,
                },
              },
            }"
          >
            {{ t('the') }}
            <span
              class="inline-flex justify-center items-center bg-[#B696FF] text-[#0D2266] h-11.5 rounded-full px-4 lg:h-17.5"
              >{{ t('future') }}</span
            >
            of
          </div>
          <div
            v-motion="{
              initial: {
                opacity: 0,
              },
              enter: {
                opacity: 1,
                transition: {
                  delay: 1000,
                },
              },
            }"
          >
            {{ t('content') }}
          </div>
          <div
            v-motion="{
              initial: {
                opacity: 0,
              },
              enter: {
                opacity: 1,
                transition: {
                  delay: 1500,
                },
              },
            }"
          >
            {{ t('monetization-is-here') }}
          </div>
        </div>
      </div>
    </div>
  </section>
  <section>
    <div class="lg:max-w-210.5 flex flex-col items-center mx-auto mt-28">
      <div class="text-2xl tracking-wide text-center px-10 sm:text-[1.75rem]">
        <span class="text-[#7540EE]">{{ t('empower-your-creativity') }}</span>
        {{ t('with-the-power-of-nfts') }}
      </div>
      <div
        class="mt-12.5 max-w-144.5 text-center tracking-wide text-lg px-5 sm:px-0"
      >
        {{ t('platforms-like-patreon') }}
      </div>
      <div
        class="h-109 overflow-hidden flex justify-between items-start w-full mt-5.5"
      >
        <div class="-translate-x-12.5 lg:translate-x-0">
          <img
            alt="t('bool')"
            src="~/assets/images/token-gated-chats-and-streams/bool.svg"
            class="element w-58 h-58 lg:w-99 lg:h-99"
            :style="elementStyle(0.4)"
          />
        </div>
        <div class="translate-x-8.5 lg:translate-x-0">
          <img
            alt="t('bool-0')"
            src="~/assets/images/token-gated-chats-and-streams/bool-2.svg"
            class="element-2 w-32 h-32"
            :style="elementStyle(0.4)"
          />
        </div>
      </div>
    </div>
    <div class="hr hidden lg:flex" />
  </section>

  <section>
    <WhyBelong
      :data="chooseItems"
      :title="t('why-belong-is-the-ultimate-platform-for-creators')"
      class="pb-21.5 mt-13"
    />
  </section>

  <section>
    <div class="hr" />
    <div
      class="flex flex-col mt-4 pb-13 lg:flex-row-reverse lg:justify-between lg:max-w-256 lg:px-5 lg:mx-auto lg:pb-15 lg:mt-20 xl:px-0"
    >
      <div class="block-mobile-with-shadow">
        <NuxtImg
          loading="lazy"
          alt="t('token-gating-for-telegram-and-whatsapp')"
          src="/images/token-gated-chats-and-streams/token-gating.webp"
          class="h-79.25 w-66.5 object-cover object-top mx-auto lg:w-66.5 lg:h-112 lg:mx-0"
        />
      </div>

      <div class="max-w-166 mx-auto mt-6 lg:mx-0">
        <div
          class="h-8 w-45.5 bg-[#0B0B0B] rounded-5 flex items-center justify-center text-[#FFB0F0] mx-auto mt-2.5 lg:mt-0 lg:mx-0"
        >
          {{ t('coming-soon') }}
        </div>
        <div
          class="mt-4 text-center tracking-wide text-[1.75rem] mx-7 lg:text-left lg:mx-0"
        >
          {{ t('token-gating-for-telegram-and-whatsapp-0') }}
        </div>
        <div
          class="mt-5.5 mx-5 text-center text-[#cecece] tracking-wide text-lg leading-normal lg:text-left lg:mx-0 lg:text-white"
        >
          <p class="max-w-153.5">
            {{
              t(
                'already-have-a-thriving-community-on-telegram-or-whatsapp-soon-with-belongs-token-gating-youll-be-able-to-monetize-these-platforms-effortlessly-use-your-existing-tools-and-integrate-belongs-token-gating-to-generate-revenue-from-your-telegram-channels-and-whatsapp-chats'
              )
            }}
          </p>

          <a
            href="https://x9sujmtmm4g.typeform.com/to/Ur2S9b4m"
            target="_blank"
            class="w-35 h-12 bg-[#7540EE] flex items-center justify-center font-600 text-base tracking-wide capitalize min-w-35 rounded-full text-white mt-12.5 mx-auto"
            style="-webkit-tap-highlight-color: transparent"
          >
            {{ t('enter') }}
          </a>
        </div>
      </div>
    </div>
    <div class="hr" />
  </section>
  <section>
    <div
      class="pt-13 flex flex-col lg:flex-row lg:py-40 lg:justify-between lg:max-w-272.5 lg:mx-auto"
    >
      <div class="flex flex-col items-center mx-3.5">
        <img
          alt="t('referring-creators-to-belong')"
          class="mx-auto w-28.5 h-25.5"
          src="~/assets/images/token-gated-chats-and-streams/referring-creators.svg"
        />
        <div class="hidden lg:flex flex-col">
          <div
            class="bg-black rounded-full py-3.5 pl-6 pr-6.5 flex flex-row justify-between max-w-96.5 w-full tracking-wide font-base font-600 capitalize mt-10"
          >
            {{ t('get-this-for-free') }}
            <span class="text-[#FFB0F0] font-400 tracking-normal">$0.00</span>
          </div>
          <div class="mt-2 text-xs tracking-wide text-center text-[#626265]">
            {{ t('invite-10-users-to-get-whitelisted-and-get-this-for-free') }}
            <div class="mt-1">
              <span class="text-white">{{ t('invited-4') }}</span
              >{{ t('10-users') }}
            </div>
          </div>
        </div>
      </div>
      <div class="text-center lg:max-w-130 lg:text-left">
        <div class="tracking-wide text-[1.75rem] mx-5 mt-10 lg:m-0">
          <span class="text-[#7540EE]">{{ t('referring-creators') }}</span>
          {{ t('to-belong') }}
        </div>
        <div
          class="tracking-wide text-lg leading-normal text-[#cecece] mt-7.5 mx-5 lg:mx-0 lg:mt-9"
        >
          {{
            t(
              'know-other-creators-introduce-them-to-belong-and-benefit-from-their-success-with-our-referral-system-earn-a-3-commission-instantly-with-every-membership-pass-sale-they-make'
            )
          }}
        </div>
      </div>
    </div>
  </section>
  <section>
    <div
      class="bg-[#000000] rounded-5 mx-5 h-111.5 overflow-hidden relative mt-15 max-w-309.5 md:h-92.5 md:mt-17.5 md:mb-14.5 xl:mx-auto"
    >
      <div
        class="seamless-transition max-w-95.5 mx-auto h-full text-center relative z-1 flex flex-col items-center justify-center md:max-w-192.5 md:px-3.5 !md:mt-31"
      >
        <!-- mt-21.5 -->
        <div class="tracking-wide text-[1.75rem] px-3.5 sm:px-0">
          <span class="text-[#FFB0F0]">{{
            t('seamless-transition-to-web3')
          }}</span>
          {{ t('with-belong') }}
        </div>
        <div
          class="tracking-wide text-lg leading-normal mt-7.5 px-3.5 sm:px-0 md:mt-8.5"
        >
          {{
            t(
              'embrace-the-decentralized-world-of-web3-with-belong-you-and-your-fans-get-an-unmatched-content-engagement-experience'
            )
          }}
        </div>
      </div>
      <NuxtImg
        loading="lazy"
        format="webp"
        alt="t('seamless-transition-to-web3-with-belong')"
        class="object-cover w-full h-full object-bottom absolute left-0 right-0 top-0"
        src="/images/token-gated-chats-and-streams/seamless-transition.png"
      />
    </div>
  </section>

  <section>
    <div
      class="relative flex flex-col lg:flex-row-reverse lg:justify-between lg:max-w-277 lg:mx-auto lg:px-5 lg:pt-8 lg:pb-10.5 xl:px-0"
    >
      <NuxtImg
        loading="lazy"
        alt="t('are-you-ready-to-transform-your-creator-experience')"
        class="w-72 h-70.5 mx-auto absolute top-7 left-0 right-0 object-cover lg:static lg:mx-0 lg:w-131.5 lg:h-128.5"
        src="/images/token-gated-chats-and-streams/creator-experience.webp"
      />
      <div
        class="mx-5 text-center pt-68 relative z-2 lg:max-w-125 lg:pt-0 lg:text-left lg:mx-0 lg:mt-30.5"
      >
        <div class="tracking-wide text-[1.5rem]">
          {{ t('are-you-ready-to-transform-your-creator-experience-0') }}
        </div>
        <div class="tracking-wide text-lg leading-normal mt-6.5 lg:mt-9">
          {{
            t(
              'belong-isnt-just-another-platform-its-a-movement-join-us-and-be-a-part-of-the-future'
            )
          }}
        </div>
        <a
          href="https://app.belong.net/"
          target="_blank"
          class="flex items-center justify-center mx-auto bg-[#7540EE] max-w-61 w-full h-12 font-600 text-base tracking-wide rounded-full mt-10.5 lg:mt-12.5 lg:mx-0"
        >
          {{ t('continue') }}
        </a>
      </div>
    </div>
  </section>
  <section>
    <InstallSection class="px-5 pt-30 lg:pt-25" />
  </section>
</template>

<style scoped>
.show-content {
  @apply opacity-0 animate-duration-9000;
}

.show-content-1 {
  animation-name: fadeInOut1;
  animation-delay: 0s;
}

.show-content-2 {
  animation-name: fadeInOut2;
  animation-delay: 3s;
}

.show-content-3 {
  animation-name: fadeInOut3;
  animation-delay: 6s;
}

@keyframes fadeInOut1 {
  0%,
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes fadeInOut2 {
  0%,
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes fadeInOut3 {
  0%,
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes fade {
  0% {
    object-position: right;
  }
  100% {
    object-position: left;
  }
}

@media (min-width: 1024px) {
  .block-mobile-with-shadow {
    &::after {
      @apply hidden;
    }
  }
}

@media (min-width: 375px) {
  .animation-block-text {
    @apply text-[2.625rem];
  }
  .seamless-transition {
    @apply mt-21.5 h-auto;
  }
}

.element {
  transition: transform 0.2s linear;
}

.element-2 {
  transition: transform 0.1s linear;
}
</style>
